import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getActiveKioskCompany, setActiveKioskCompany } from "@/lib/kioskSession";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);
const MOCK_MODE = process.env.REACT_APP_MOCK_MODE === "true";
const MOCK_USER = { id: "demo-user", name: "المدير العام الرئيسي التجريبي", email: "demo@example.com", role: "super_admin", is_root: true };
const MOCK_COMPANIES = [
  { id: "demo-pharma-a", name: "صيدلية الشفاء التجريبية", base_currency: "SYP", business_type: "pharmacy", subscription_plan: "accounting_ecommerce" },
  { id: "demo-pharma-b", name: "صيدلية الأمل التجريبية", base_currency: "SYP", business_type: "pharmacy", subscription_plan: "accounting_ecommerce" },
  { id: "demo-restaurant", name: "مطعم المذاق التجريبي", base_currency: "SYP", business_type: "restaurant", subscription_plan: "accounting_ecommerce" },
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyIdState] = useState(localStorage.getItem("company_id") || null);
  const [loading, setLoading] = useState(true);

  const setCompanyId = (cid) => {
    setCompanyIdState(cid);
    if (cid) localStorage.setItem("company_id", cid);
    else localStorage.removeItem("company_id");
  };

  const bootstrap = async () => {
    if (MOCK_MODE) {
      // The public preview is a non-root company admin, allowing feature restrictions
      // to be tested without bypassing the root-admin safeguards.
      setUser(MOCK_USER); setCompanies(MOCK_COMPANIES);
      if (!companyId || !MOCK_COMPANIES.some(c => c.id === companyId)) setCompanyId(MOCK_COMPANIES[0].id);
      setLoading(false);
      return;
    }
    try {
      const { data: u } = await api.get("/auth/me");
      setUser(u);
      const { data: cs } = await api.get("/companies");
      setCompanies(cs);
      if (!companyId && cs.length > 0) setCompanyId(cs[0].id);
      if (companyId && !cs.find((c) => c.id === companyId)) setCompanyId(cs[0]?.id || null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const kioskRoute = window.location.pathname.match(/^\/kiosk\/([^/]+)/);
    const activeKiosk = getActiveKioskCompany();
    if (kioskRoute) setActiveKioskCompany(decodeURIComponent(kioskRoute[1]));
    if (kioskRoute || activeKiosk) {
      // The customer-facing tab never bootstraps or displays an employee session.
      setUser(null); setCompanies([]); setCompanyIdState(null); setLoading(false);
      return;
    }
    bootstrap();
  }, []); // eslint-disable-line

  const login = async (email, password, totp_code, recovery_code) => {
    if (MOCK_MODE) {
      if (email !== "demo@example.com" || password !== "Demo1234") throw new Error("بيانات الدخول التجريبية غير صحيحة");
      localStorage.setItem("access_token", "demo-token");
      setUser(MOCK_USER); setCompanies(MOCK_COMPANIES); setCompanyId(MOCK_COMPANIES[0].id);
      return MOCK_USER;
    }
    const body = { email, password };
    if (recovery_code) body.recovery_code = recovery_code;
    else if (totp_code) body.totp_code = totp_code;
    const { data } = await api.post("/auth/login", body);
    if (data.warning) { try { const { toast } = await import("sonner"); toast.warning(data.warning); } catch {} }
    if (data.token) localStorage.setItem("access_token", data.token);
    setUser(data.user);
    const { data: cs } = await api.get("/companies");
    setCompanies(cs);
    if (cs.length > 0) setCompanyId(cs[0].id);
    return data.user;
  };

  const logout = async () => {
    if (MOCK_MODE) {
      localStorage.removeItem("access_token"); localStorage.removeItem("company_id");
      setUser(null); setCompanies([]); setCompanyIdState(null); return;
    }
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("access_token");
    localStorage.removeItem("company_id");
    setUser(null); setCompanies([]); setCompanyIdState(null);
  };

  const refreshCompanies = async () => {
    if (MOCK_MODE) { setCompanies(MOCK_COMPANIES); return MOCK_COMPANIES; }
    const { data: cs } = await api.get("/companies");
    setCompanies(cs);
    return cs;
  };

  const currentCompany = companies.find((c) => c.id === companyId) || null;

  return (
    <AuthCtx.Provider value={{ user, companies, companyId, setCompanyId, currentCompany, loading, login, logout, refreshCompanies }}>
      {children}
    </AuthCtx.Provider>
  );
}
